import { defineStore } from 'pinia';
import { Draft } from '@/dashboard/types/draft';
import { calculateInscribedSize, calculatePositiveOriginalPoint } from '@/dashboard/helper';
import { instance } from '@/dashboard/api/instance';
import { showNotification } from '@/dashboard/vue/notifications/notification_actions';
import { isSmartphone } from '@/dashboard/constants/ui';
import { DraftsApi } from '@/dashboard/api/drafts';
import { useDraftStore } from './draftStore';

export type DraftImageStoreState = {
  draft: Draft | null;
  points: any[];
  deletedPoints: any[];
  deletedComments: any[];
  originalImageSize: {
    width: number;
    height: number;
  };
  imageSize: {
    width: number;
    height: number;
  };
  blurImageSize: {
    width: number;
    height: number;
  };
  imageScaledSize: {
    width: number;
    height: number;
  };
  defaultColor: number;
  showNewDiscussionMessage: boolean;
  messengerLoading: boolean;
  drawingPoint: any | null;
  activePoint: any | null;
  hoverPoint: any | null;
  undoDrawingPoint: any | null;
  undoActivePoint: any | null;
  rightMousePoint: any | null;
  canCloseTempPoint: boolean;
  text: string;
  showHeaderMenu: boolean;
  showBottomHelp: boolean;
  commentMenuTop: number | null;
  commentMenuId: number | null;
  commentMenuUserId: number | null;
  showColorPicker: boolean;
  hideAll: boolean;
};

export const useDraftImageStore = defineStore('draftImageStore', {
  state: (): DraftImageStoreState => {
    const defaultColor = localStorage.getItem('defaultColor');

    const image_width = (document.querySelector("input[name='image_width']") as any)?.value;
    const image_height = (document.querySelector("input[name='image_height']") as any)?.value;
    const original_image_width = (document.querySelector("input[name='original_image_width']") as any)?.value;
    const original_image_height = (document.querySelector("input[name='original_image_height']") as any)?.value;

    return {
      draft: null,
      points: [],
      deletedPoints: [],
      deletedComments: [],
      originalImageSize: {
        width: original_image_width ? +original_image_width : 0,
        height: original_image_height ? +original_image_height : 0,
      },
      imageSize: {
        width: image_width ? +image_width : 0,
        height: image_height ? +image_height : 0,
      },
      blurImageSize: {
        width: 0,
        height: 0,
      },
      imageScaledSize: {
        width: 0,
        height: 0,
      },
      defaultColor: defaultColor ? +defaultColor : 10,
      showNewDiscussionMessage: true,
      messengerLoading: false,
      drawingPoint: null,
      activePoint: null,
      hoverPoint: null,
      undoDrawingPoint: null,
      undoActivePoint: null,
      rightMousePoint: null,
      canCloseTempPoint: true,
      text: '',
      showHeaderMenu: false,
      showBottomHelp: false,
      commentMenuTop: null,
      commentMenuId: null,
      commentMenuUserId: null,
      showColorPicker: false,
      hideAll: false,
    };
  },
  getters: {
    imageZoom(): number {
      return this.originalImageSize.width / this.imageSize.width;
    },
    blurImageUrl(): string | null {
      const blurImage = document.querySelector("input[name='blur_image']") as HTMLInputElement | null;
      return blurImage?.value ?? null;
    },
    blurPercent(): number {
      return (this.imageScaledSize.height / this.imageScaledSize.width) * 100;
    },
  },
  actions: {
    setText(text: string) {
      this.text = text;
    },
    setPoints(points: any[]) {
      this.points = points.map((item: any) => {
        return {
          ...item.point,
          left: (item.point.left * 100) / this.originalImageSize.width,
          top: (item.point.top * 100) / this.originalImageSize.height,
          width: item.point.width,
          height: item.point.height,
          comments: item.comments,
          approve: item.approve,
          read: item.read,
        };
      });
    },
    setDeletedPoints(deletedPoints: any[]) {
      this.deletedPoints = deletedPoints;
    },
    setDeletedComments(deletedComments: any[]) {
      this.deletedComments = deletedComments;
    },
    setImageSize(imageSize: { width: number; height: number }) {
      this.imageSize = imageSize;
    },
    setBlurImageSize(blurImageSize: { width: number; height: number }) {
      this.blurImageSize = blurImageSize;
    },
    setDrawingPoint(drawingPoint: any | null) {
      this.drawingPoint = drawingPoint;
    },
    setActivePoint(point: any | null) {
      this.activePoint = point;
      this.commentMenuTop = null;
      this.setShowColorPicker(false);

      if (point?.id === this.hoverPoint?.id) {
        this.hoverPoint = null;
      }
    },
    setHoverPoint(point: any | null) {
      if (!!point && point?.id !== this.activePoint?.id) {
        this.hoverPoint = point;
      } else {
        this.hoverPoint = null;
      }
    },
    setShowHeaderMenu(show: boolean) {
      this.showHeaderMenu = show;
    },
    setShowBottomHelp(show: boolean) {
      this.showBottomHelp = show;
    },
    setCommentMenuTop(top: number | null) {
      this.commentMenuTop = top;
    },
    setCommentMenuId(id: number | null) {
      this.commentMenuId = id;
    },
    setCommentMenuUserId(userId: number | null) {
      this.commentMenuUserId = userId;
    },
    setShowColorPicker(show: boolean) {
      this.showColorPicker = show;
    },
    setDefaultColor(color: number) {
      this.defaultColor = color;
      localStorage.setItem('defaultColor', String(color));
    },
    setHideAll(hide: boolean) {
      this.hideAll = hide;
    },
    setCanCloseTempPoint(canClose: boolean) {
      this.canCloseTempPoint = canClose;
    },
    setUndoTempPoint(payload: { drawingPoint: any | null; activePoint: any | null }) {
      this.undoDrawingPoint = payload.drawingPoint;
      this.undoActivePoint = payload.activePoint;
    },
    setRightMousePoint(point: any | null) {
      this.rightMousePoint = point;
    },
    calculateSize(scaleDownMode: boolean) {
      this.imageScaledSize = calculateInscribedSize(this.imageSize, this.originalImageSize, scaleDownMode);
    },
    async loadDraft(id: any) {
      const draftStore = useDraftStore();
      const result = await DraftsApi.get(id);
      if (result.success && result.draft) {
        const draft = result.draft;
        const points = result.points.map((item: any) => {
          return {
            ...item.point,
            left: (item.point.left * 100) / this.originalImageSize.width,
            top: (item.point.top * 100) / this.originalImageSize.height,
            width: item.point.width,
            height: item.point.height,
            comments: item.comments,
            approve: item.approve,
            read: item.read,
          };
        });

        this.setPoints(result.points);
        // Очищаем удаленные точки и комментарии при загрузке нового черновика
        this.setDeletedPoints([]);
        this.setDeletedComments([]);
        draftStore.setDraft(draft);
        draftStore.followed = result.followed;

        const urlResponse = await DraftsApi.getDownloadUrl({
          short_link: draft.short_link,
          filename: encodeURIComponent(draft.name),
        });
        draftStore.downloadUrl = urlResponse.url;
      }
    },
    createTempPoint(payload: any) {
      if (this.messengerLoading) {
        return;
      }
      const { size, location, position } = payload;
      const defaultColor = this.defaultColor;
      const points = this.points;
      const draftStore = useDraftStore();
      const draft = draftStore.draft;
      if (!draft) return;

      const scaled = draftStore.imageScaleDownMode;
      const imageSize = calculateInscribedSize(this.imageSize, this.originalImageSize, scaled);
      const orders = points
        .filter((item: any) => {
          return [...this.deletedPoints].indexOf(item.id) === -1;
        })
        .map((item: any) => {
          return item.order;
        });
      const point = {
        created_at: null,
        order: orders.length > 0 ? Math.max(...orders) + 1 : 1,
        top: location.y,
        left: location.x,
        width: (size.width * 100) / imageSize.width,
        height: (size.height * 100) / imageSize.height,
        position: position,
        draft_id: draft.id,
        color: defaultColor,
        comments: [],
      };

      const original = calculatePositiveOriginalPoint(
        {
          x: point.left,
          y: point.top,
        },
        this.imageSize,
        this.originalImageSize,
        imageSize,
        scaled
      );

      const drawingPoint = {
        ...point,
        left: (point.left * 100) / imageSize.width,
        top: (point.top * 100) / imageSize.height,
      };

      const activePoint = {
        ...point,
        left: drawingPoint.left,
        top: drawingPoint.top,
        left_original: original.x,
        top_original: original.y,
      };

      this.setDrawingPoint(drawingPoint);
      this.setActivePoint(activePoint);
      this.setUndoTempPoint({ drawingPoint: null, activePoint: null });
    },
    closeTempPoint() {
      const drawingPoint = this.drawingPoint;
      const activePoint = this.activePoint;

      if (this.messengerLoading || !activePoint) {
        return;
      }

      if (this.canCloseTempPoint) {
        this.setUndoTempPoint({ drawingPoint, activePoint });
        this.setDrawingPoint(null);
        this.setActivePoint(null);

        const text = this.text;
        if (text.length > 4) {
          showNotification('any_type', {
            emoji: '❌',
            title: isSmartphone.value ? 'Point deleted' : 'The point has been permanently deleted',
            ok_action: isSmartphone.value ? undefined : () => {},
            undo_action: async () => {
              setTimeout(() => {
                const event = new CustomEvent('setMessengerText', { detail: text });
                window.dispatchEvent(event);
              }, 0);
              await this.undoTempPoint();
            },
          });
        }
        this.setText('');
      } else {
        const event = new CustomEvent('closeTempPoint', { detail: { drawingPoint, activePoint } });
        window.dispatchEvent(event);
        this.setCanCloseTempPoint(true);
        this.setUndoTempPoint({ drawingPoint: null, activePoint: null });
      }
    },
    undoTempPoint() {
      const drawingPoint = this.undoDrawingPoint;
      const activePoint = this.undoActivePoint;
      this.setDrawingPoint(drawingPoint);
      this.setActivePoint(activePoint);
      this.setUndoTempPoint({ drawingPoint: null, activePoint: null });
    },
    async createPoint(text: string) {
      const point = this.activePoint;

      if (!point || point.created_at) {
        return;
      }

      const formData = new FormData();
      formData.append('point[draft_id]', point.draft_id);
      formData.append('point[top]', point.top_original ? point.top_original : point.top);
      formData.append('point[left]', point.left_original ? point.left_original : point.left);
      formData.append('point[width]', point.width);
      formData.append('point[height]', point.height);
      formData.append('point[order]', point.order);
      formData.append('point[position]', point.position);
      formData.append('point[pending]', 'true');
      formData.append('point[color]', point.color);
      formData.append('point[comment]', text);

      try {
        this.messengerLoading = true;
        await instance.post(`/drafts/${point.draft_id}/create_point`, formData);
        const draftStore = useDraftStore();
        if (draftStore.draft) {
          await this.loadDraft(draftStore.draft.id);
        }

        const last = this.points[this.points.length - 1];
        if (last) {
          // Удаляем точку из deletedPoints, если она там была (на случай undo)
          const deletedPoints = this.deletedPoints.filter((id: any) => id !== last.id);
          this.setDeletedPoints(deletedPoints);
        }
        this.setActivePoint(last);
        this.setDrawingPoint(null);
        this.messengerLoading = false;

        if (
          (window as any).g_opt.user.id &&
          draftStore.draft &&
          draftStore.draft.owner_id !== (window as any).g_opt.user.id &&
          !draftStore.followed
        ) {
          await draftStore.followDraft();
        }
      } catch (e) {
        this.messengerLoading = false;
      }
    },
    deletePoint(id: any) {
      const deletedPoints = [...this.deletedPoints, id];
      this.setDeletedPoints(deletedPoints);
      this.setDrawingPoint(null);
      this.setActivePoint(null);
    },
    undoDeletePoint(id: any) {
      const deletedPoints = this.deletedPoints.filter((item: any) => item !== id);
      this.setDeletedPoints(deletedPoints);
    },
    async approvePoint() {
      const point = this.activePoint;
      if (!!point && point.id) {
        const formData = new FormData();
        const approve = point.approve;
        formData.append('completed', approve ? 'no' : 'yes');
        await instance.put(`/points/${point.id}/complete`, formData);

        const activePoint = { ...this.activePoint, approve: !approve };
        const points = this.points.map((item: any) => {
          if (item.id === activePoint.id) {
            return { ...item, approve: !approve };
          }
          return item;
        });

        this.setActivePoint(activePoint);
        this.points = points;
      }
    },
    async readPointComments() {
      const activePoint = this.activePoint;
      const lastComment = activePoint ? activePoint.comments.slice(-1) : null;
      if (lastComment && activePoint) {
        const formData = new FormData();
        formData.append('last_id', lastComment.id);
        await instance.put(`/points/${activePoint.id}/read`, formData);
        const active = { ...activePoint, read: true };
        const points = this.points.map((item: any) => {
          if (item.id === activePoint.id) {
            return { ...item, read: true };
          }
          return item;
        });

        this.setActivePoint(active);
        this.points = points;

        setTimeout(() => {
          const point = this.points.find((item: any) => item.id === activePoint.id);
          if (point) {
            point.comments = point.comments.map((item: any) => {
              return { ...item, read: true };
            });
            if (this.activePoint && this.activePoint.id === point.id) {
              this.setActivePoint(point);
            }
          }
        }, 4000);
      }
    },
    async createComment(payload: { pointId: any; text: string }) {
      const { pointId, text } = payload;
      const formData = new FormData();
      formData.append('text', text);
      const response = await instance.post(`/points/${pointId}/comments.json`, formData);
      const point = this.points.find((item: any) => {
        return item.id === pointId;
      });
      if (!point) return;

      const newComment = { comment: response.data.comment, read: false, sender: response.data.sender };
      const commentId = response.data.comment.id;

      // Удаляем комментарий из deletedComments, если он там был (на случай undo)
      const deletedComments = this.deletedComments.filter((id: any) => id !== commentId);
      this.setDeletedComments(deletedComments);

      const comments = [...(point.comments || []), newComment];
      const points = this.points.map((item: any) => {
        if (item.id === pointId) {
          return { ...item, comments };
        }
        return item;
      });

      this.points = points;

      const draftStore = useDraftStore();
      if ((window as any).g_opt.user.id && draftStore.draft && draftStore.draft.owner_id !== (window as any).g_opt.user.id && !draftStore.followed) {
        await draftStore.followDraft();
      }

      setTimeout(() => {
        const point = this.points.find((item: any) => item.id === pointId);
        if (point) {
          const updatedPoints = this.points.map((item: any) =>
            item.id === pointId
              ? {
                  ...item,
                  comments: item.comments.map((commentItem: any) => {
                    if (commentItem.comment && typeof commentItem.comment === 'object') {
                      return { ...commentItem, read: true };
                    }
                    return commentItem;
                  }),
                }
              : item
          );
          this.points = updatedPoints;
        }
      }, 1000);
    },
    deleteComment(id: any) {
      const deletedComments = [...this.deletedComments, id];
      this.setDeletedComments(deletedComments);
      this.setCommentMenuTop(null);
      this.setCommentMenuId(null);
    },
    copyComment() {
      const commentId = this.commentMenuId;
      const shortLink = (document.querySelector("input[name='short_link']") as any).value;
      const link = `${window.location.protocol}//${window.location.host}/${shortLink}#${commentId}`;
      navigator.clipboard.writeText(link);
    },
    undoDeleteComment(id: any) {
      const deletedComments = this.deletedComments.filter((item: any) => item !== id);
      this.setDeletedComments(deletedComments);
    },
    toggleHeaderMenu(value?: boolean) {
      const currentValue = this.showHeaderMenu;
      this.setShowHeaderMenu(value !== undefined ? value : !currentValue);
    },
    toggleBottomHelp(value?: boolean) {
      const currentValue = this.showBottomHelp;
      this.setShowBottomHelp(value !== undefined ? value : !currentValue);
    },
    setCommentMenu(payload: { top: number | null; id: number | null; userId: number | null }) {
      const { top, id, userId } = payload;
      this.setCommentMenuTop(top);
      this.setCommentMenuId(id);
      this.setCommentMenuUserId(userId);
    },
    toggleColorPicker(value?: boolean) {
      const currentValue = this.showColorPicker;
      this.setShowColorPicker(value !== undefined ? value : !currentValue);
    },
    async changeColor(payload: { colorNumber: number; pointId?: number }) {
      const { colorNumber, pointId } = payload;
      const points = this.points;
      const drawingPoint = this.drawingPoint;
      const activePoint = this.activePoint;

      if (pointId) {
        const active = { ...activePoint, color: colorNumber };
        const newPoints = points.map((item: any) => {
          return item.id === active.id ? active : item;
        });
        this.setActivePoint(active);
        this.points = newPoints;

        const formData = new FormData();
        formData.append('point[color]', String(colorNumber));
        await instance.put(`/points/${pointId}`, formData);
      } else {
        const drawing = { ...drawingPoint, color: colorNumber };
        const active = { ...activePoint, color: colorNumber };
        this.setDrawingPoint(drawing);
        this.setActivePoint(active);
      }

      this.setDefaultColor(colorNumber);
    },
    openCommentHotkey() {
      const points = this.points;
      const activePoint = this.activePoint;
      const deletedPoints = this.deletedPoints;

      const getIndex = (startIndex: any): number => {
        let newIndex = +startIndex + 1 < points.length ? +startIndex + 1 : 0;
        if (deletedPoints.indexOf(points[newIndex].id) > -1) {
          newIndex = getIndex(newIndex);
        }
        return newIndex;
      };

      if (points.length <= 0) {
        return;
      }

      if (activePoint) {
        let index = points.map((item: any) => item.id).indexOf(activePoint.id);
        index = getIndex(index);
        const point = points[index];
        this.setActivePoint(point);
      } else {
        const index = getIndex(-1);
        const point = points[index];
        this.setActivePoint(point);
      }

      setTimeout(() => {
        const chat = document.querySelector('.messenger');
        chat?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 20);
    },
  },
});
